import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { clientApi, QuestionSetStatus } from '@/api/client'
import { qk } from '@/api/queryKeys'
import { streamSse } from '@/api/sse'
import DownloadReportButton from '@/components/DownloadReportButton'
import ErrorBox from '@/components/ErrorBox'
import Skeleton from '@/components/Skeleton'
import Topbar from '@/components/Topbar'
import { timeAgo } from '@/utils/format'

function sourceContextForItem(setStatus: QuestionSetStatus | null, item: any): any[] {
  const sources = Array.isArray(setStatus?.sources) ? setStatus.sources : []
  const ids = Array.isArray(item?.source_asset_ids_json) ? item.source_asset_ids_json : []
  if (!sources.length || !ids.length) return []
  const wanted = new Set(ids.map((id: any) => String(id || '').trim()).filter(Boolean))
  return sources.filter((src: any) => wanted.has(String(src?.source_asset_id || '').trim()))
}

function summaryText(setStatus: QuestionSetStatus | null): string | null {
  const qaSummary = String(setStatus?.summary_json?.generation?.qa_summary || '').trim()
  if (qaSummary) return qaSummary
  const generated = setStatus?.generated_count || 0
  const forecasted = setStatus?.forecasted_count || 0
  const failed = setStatus?.failed_count || 0
  if (!setStatus) return null
  return `Generated ${generated} question${generated === 1 ? '' : 's'} from ${setStatus.sources.length} source${setStatus.sources.length === 1 ? '' : 's'}, with ${forecasted} forecasted and ${failed} failed.`
}

function probabilityLabel(item: any): string | null {
  const raw = item?.forecast_output_json?.probability
  if (typeof raw === 'number') return `${Math.round(raw * 100)}%`
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  return null
}

export default function QuestionSetDetail() {
  const { setId } = useParams()
  const queryClient = useQueryClient()
  const [sseOnline, setSseOnline] = useState(false)
  const setQuery = useQuery({
    queryKey: qk.questionSets.status(setId || ''),
    queryFn: () => clientApi.questionSetStatus(setId as string),
    enabled: !!setId,
    // Stop polling once the set terminates to avoid pointless background
    // traffic when the user leaves the tab open on a completed set. When
    // SSE is online we widen the poll interval to 30s as a safety net
    // (in case the stream dies silently).
    refetchInterval: (query) => {
      const status = String((query.state.data as QuestionSetStatus | undefined)?.status || '').toLowerCase()
      if (status === 'succeeded' || status === 'failed' || status === 'completed') return false
      return sseOnline ? 30_000 : 5_000
    },
    staleTime: 1_000,
  })
  const data = setQuery.data ?? null
  const error = setQuery.error
  const loading = setQuery.isLoading

  // Live stream. Updates the react-query cache in-place so the rest of
  // the component doesn't need to know whether data came from a poll
  // response or an SSE frame. Falls back cleanly: on stream close we
  // clear `sseOnline` so the poll resumes at the tight 5s cadence.
  useEffect(() => {
    if (!setId) return
    const key = qk.questionSets.status(setId)
    const stop = streamSse<QuestionSetStatus>(
      `/v1/question-sets/${encodeURIComponent(setId)}/stream`,
      {
        onEvent: (evt) => {
          if (evt.type === 'status' && evt.data) {
            setSseOnline(true)
            queryClient.setQueryData(key, evt.data)
          }
        },
        onClose: () => {
          setSseOnline(false)
        },
      },
    )
    return () => stop()
  }, [setId, queryClient])

  const summary = useMemo(() => summaryText(data), [data])
  const items = Array.isArray(data?.items) ? data!.items : []
  const sources = Array.isArray(data?.sources) ? data!.sources : []

  return (
    <div className="container">
      <Topbar subtitle="Source Set" />

      {loading && !error && (
        <div className="grid" style={{ marginTop: 16 }}>
          <Skeleton variant="card" count={3} />
        </div>
      )}

      {!loading && data && (
        <div className="grid">
          <div className="card">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <h2 style={{ margin: 0 }}>Source Forecast Set</h2>
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Created {data.created_at ? timeAgo(data.created_at) : 'recently'}
                  {data.completed_at ? ` · Completed ${timeAgo(data.completed_at)}` : ''}
                  {data.updated_at && !data.completed_at ? ` · Updated ${timeAgo(data.updated_at)}` : ''}
                </div>
              </div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className={`pill ${data.status === 'succeeded' ? 'ok' : data.status === 'failed' ? 'bad' : ''}`}>{data.status}</span>
                <span className="pill">{data.generated_count} generated</span>
                <span className="pill">{data.forecasted_count} forecasted</span>
                {data.failed_count > 0 ? <span className="pill bad">{data.failed_count} failed</span> : null}
                {setId ? <DownloadReportButton kind="source_set" targetId={setId} /> : null}
              </div>
            </div>
            {summary && (
              <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.6 }}>
                {summary}
              </div>
            )}
            {data.error && <div className="muted" style={{ marginTop: 12, color: 'var(--bad)' }}>{data.error}</div>}
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>Sources</h2>
            <div className="stack" style={{ gap: 10 }}>
              {sources.map((src: any) => {
                const href = src?.source_type === 'url' ? String(src?.input_value || '').trim() : ''
                const label = String(src?.source_label || src?.input_value || src?.source_asset_id || 'Source').trim()
                return (
                  <div key={src?.source_asset_id || label} className="signal-row">
                    <div style={{ fontWeight: 700 }}>{label}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {src?.source_type || 'source'}
                      {src?.ingested_at ? ` · ingested ${timeAgo(src.ingested_at)}` : ''}
                    </div>
                    {href ? (
                      <div style={{ marginTop: 8 }}>
                        <a href={href} target="_blank" rel="noreferrer">Open source</a>
                      </div>
                    ) : (
                      <div className="muted" style={{ fontSize: 12, marginTop: 8, whiteSpace: 'pre-wrap' }}>{String(src?.input_value || '').slice(0, 400)}</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>Generated Questions</h2>
            <div className="stack" style={{ gap: 0 }}>
              {items.map((item: any, idx: number) => {
                const q = item?.question_spec_json || {}
                const linkedSources = sourceContextForItem(data, item)
                const prob = probabilityLabel(item)
                return (
                  <div key={item?.set_item_id || idx} className="signal-row">
                    <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, lineHeight: 1.45 }}>{q.question_text || `Question ${idx + 1}`}</div>
                        {q.resolution_criteria ? (
                          <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>{q.resolution_criteria}</div>
                        ) : null}
                        {linkedSources.length > 0 ? (
                          <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                            From:{' '}
                            {linkedSources.map((src: any, srcIdx: number) => {
                              const href = src?.source_type === 'url' ? String(src?.input_value || '').trim() : ''
                              const label = String(src?.source_label || src?.input_value || `Source ${srcIdx + 1}`)
                              return (
                                <React.Fragment key={src?.source_asset_id || srcIdx}>
                                  {srcIdx > 0 ? ', ' : null}
                                  {href ? <a href={href} target="_blank" rel="noreferrer">{label}</a> : <span>{label}</span>}
                                </React.Fragment>
                              )
                            })}
                          </div>
                        ) : null}
                      </div>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <span className={`pill ${item?.status === 'succeeded' ? 'ok' : item?.status === 'failed' ? 'bad' : ''}`}>{item?.status || 'pending'}</span>
                        {prob ? <span className="pill">{prob}</span> : null}
                        {item?.item_id ? <Link className="btn small" to={`/items/${item.item_id}`}>View item</Link> : null}
                      </div>
                    </div>
                  </div>
                )
              })}
              {items.length === 0 ? <div className="muted" style={{ fontSize: 13 }}>No generated questions yet.</div> : null}
            </div>
          </div>
        </div>
      )}

      {error && <ErrorBox error={error} />}
    </div>
  )
}
